import React, { useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { useDeepCompareEffect, useDeepCompareMemo, useMountedEffect } from '@clatter/platform';
import {
  FolderManagerAction,
  FolderManagerActionTypes,
  FolderManagerBreadcrumb,
  FolderManagerDragStartPayload,
  FolderManagerFilters,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
  FolderManagerViewTypes,
} from '../../interfaces';
import { useUpdateAssetMutation, useUpdateFolderMutation } from '../../services';
import { assetManagerUISelectors } from '../../store';

import { ViewTypeSwitcher } from '../../shared';
import { getFolderManagerColumns, handleAssignItemsToFolder } from '../../helpers';
import { NgCardHeader, NgCardContent, IconButtonRounded } from '../UI';
import { DetailsRail } from './Components/DetailsRail';
import { FolderManagerHeader } from './Components/FolderManagerHeader';
import { FolderManagerGridView } from './Components/FolderManagerGridView';
import { FolderManagerTableView } from './Components/FolderManagerTableView';
import chevronIcon from '../../../assets/icons/chevron-light.svg';

//region INTERFACES
interface ColumnOptions {
  selectedItems: any;
  slug: string | undefined;
  onAction: ({ actionType, payload }: FolderManagerAction) => void;
  isLoading: boolean;
}

interface FolderManagerProps {
  isLoading: boolean;
  fileInputRef?: any;
  rootFolderId: number;
  noDataMessage?: string;
  selectedFolderId: any;
  //new ->
  selectedItems?: FolderManagerItem[];
  hideHeader?: boolean;
  hideDetails?: boolean;
  minHeight?: string;
  disableUpload?: boolean;
  //new <-

  hierarchyData: FolderManagerItem[];
  breadcrumbs: FolderManagerBreadcrumb[];
  defaultViewType?: FolderManagerViewTypes;

  customMultiSelectActions?: (data: {
    selectedItems: any[];
    onClear: () => void;
    onAction?: FolderManagerAction;
  }) => void;
  onAction: (data: FolderManagerAction) => void;
  allowEntityClick?: boolean;
  disableActionColumn?: boolean;
}

//endregion

//region STYLED
const StyledMainContainer = styled.div<{ $detailsOpened?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  overflow: visible;

  @media (max-width: 860px) {
    flex-direction: column;
  }

  > div:first-child {
    flex: ${({ $detailsOpened }) => ($detailsOpened ? '0 0 70%' : '0 0 100%')};
  }

  > div:nth-child(2) {
    flex: ${({ $detailsOpened }) => ($detailsOpened ? '1' : '0')};
  }
`;
//endregion

const initialFiltersState = {
  query: '',
};

const findItemRowIndex = ({ rows, item }: { rows: FolderManagerItem[]; item: FolderManagerItem }) =>
  (rows || []).findIndex((row: FolderManagerItem) => row?.id === item.id && row.type === item.type);

export const HomeFolderManager = ({
  isLoading,
  noDataMessage,
  hierarchyData = [],
  defaultViewType = FolderManagerViewTypes.GRID,
  hideHeader = false,
  allowEntityClick = false,
  disableActionColumn = false,
  minHeight,
  selectedItems: parentSelectedItems,
  hideDetails = false,
  disableUpload = true,
  onAction,
}: FolderManagerProps) => {
  const location = useLocation();
  const { slug } = useParams();

  const [updateFolder] = useUpdateFolderMutation();
  const [updateAsset] = useUpdateAssetMutation();

  const [detailsOpened, setDetailsOpened] = useState(false);
  const [activeView, setActiveView] = useState(defaultViewType);
  const [filters, setFilters] = useState<FolderManagerFilters>(initialFiltersState);
  const [localSelectedItems, setSelectedItems] = useState<FolderManagerItem[]>([]);

  const topBarRef = useRef<HTMLDivElement | null>(null);

  const searchQuery = useSelector(assetManagerUISelectors.getSearchQuery);

  const selectedItems = parentSelectedItems || localSelectedItems;

  const [rows, setRows] = useState<any>([]);

  const columns = useDeepCompareMemo(() => {
    const columns = getFolderManagerColumns({ isLoading: isLoading, selectedItems: selectedItems, slug, onAction });

    if (disableActionColumn) {
      return columns.filter((c) => c.field !== 'action');
    }

    return columns;
  }, [disableActionColumn, selectedItems, isLoading]);

  const filteredRows = useDeepCompareMemo(() => {
    if (searchQuery) {
      return rows.filter((item: any) => item.name?.toLowerCase().includes(searchQuery?.toLowerCase()));
    }
    return rows;
  }, [searchQuery, rows, slug]);
  //endregion

  const handleFiltersChange = (filters: FolderManagerFilters) => {
    setFilters((prev) => ({
      ...prev,
      query: filters?.query || '',
    }));
  };

  const handleDragStart = (data: FolderManagerDragStartPayload) => {
    if (!data || !data?.id) {
      console.log('ERROR: invalid props');
      return;
    }

    if (!selectedItems || !selectedItems.length) {
      const selectedItem = rows.find((row: any) => row.id === +data.id);

      if (!selectedItem) {
        console.error('Error, selected item data not found!');
        return;
      }

      setSelectedItems([selectedItem]);
    }
  };

  const handleDragEnd = async (payload: IFolderManagerDragEndEvent) => {
    if (slug === 'trash') {
      console.log('Trash drag actions are disabled');
      return;
    }

    // if target id folder
    if (payload.type === FolderManagerItemTypes.FOLDER) {
      const targetFolderData = rows.find((item: any) => item.id === Number(payload.id));

      if (!targetFolderData) {
        //@todo - notify user: error
        console.error('Error moving items to folder. Target folder data not found!');
        return;
      }

      const result = await handleAssignItemsToFolder({
        target: { id: targetFolderData.id, name: targetFolderData.name },
        selectedItems: selectedItems,
        updateFolderCb: updateFolder,
        updateAssetCb: updateAsset,
      });

      // @todo: notify user: success / errors
      // console.log('handleAssignItemsToFolder: result', result);

      setSelectedItems([]);
      return;
    }
  };

  const handleRowDoubleClick = ({ row }: { row: FolderManagerItem }) => {
    // handle folder click
    if (row.type === FolderManagerMimeTypes.FOLDER) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_SUB_FOLDERS, payload: row });
      return;
    }

    if (row.type === FolderManagerMimeTypes.WORKSPACE) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_WORKSPACE, payload: row });
      return;
    }

    // handle shortcut click
    if (row.sourceId) {
      if (!row?.source?.ID && !row?.source?.FolderID) {
        window.alert('Sorry original asset has been removed!');
        return;
      }

      setSelectedItems([]);
      onAction({
        actionType: FolderManagerActionTypes.GET_SUB_FOLDERS,
        payload: { id: row.source.FolderID } as FolderManagerItem,
      });
    }
  };

  const handleRowSelect = ({ event, item }: { event: any; item: any }) => {
    event.preventDefault();

    if (allowEntityClick) {
      onAction({ actionType: FolderManagerActionTypes.ROW_CLICK, payload: { id: item.id } as FolderManagerItem });
      return;
    }

    setSelectedItems((prevSelectedItems) => {
      const selectedItem = prevSelectedItems[0]; // Access the currently selected item if it exists

      // Check for shift key and only one item selected
      if (event?.shiftKey && prevSelectedItems?.length === 1 && selectedItem?.id) {
        const startRowIndex = findItemRowIndex({ rows: rows, item: selectedItem });
        const endRowIndex = findItemRowIndex({ rows: rows, item: item });

        if (startRowIndex === -1) {
          console.log('ERROR: startRowIndex not found');
          return [];
        }

        const isExpanding = startRowIndex < endRowIndex;

        // Select the range of items between start and end indices
        return rows
          .slice(isExpanding ? startRowIndex : endRowIndex, isExpanding ? endRowIndex + 1 : startRowIndex + 1)
          .filter((row: any) => !row.selectable);
      }
      // Clear selection if the clicked item is already selected
      else if (prevSelectedItems?.length === 1 && selectedItem.id === item.id && selectedItem.type === item.type) {
        return []; // Clear the selection
      }
      // Default case: select the clicked item
      else {
        return [item]; // Select the clicked item
      }
    });
  };

  //region EFFECTS
  // toggle active view
  useMountedEffect(() => {
    setActiveView(defaultViewType);
  }, [defaultViewType]);

  useDeepCompareEffect(() => {
    (() => {
      const mappedData = (hierarchyData || []).map((item: FolderManagerItem, index: number) => {
        const getStatus = () => {
          // FIXME: item.type !== FolderManagerMimeTypes.PPTX - this is temporary only for demo
          if (
            item.type === FolderManagerMimeTypes.FOLDER ||
            item.type === FolderManagerMimeTypes.WORKSPACE ||
            item.type !== FolderManagerMimeTypes.PPTX
          ) {
            return 'completed';
          }
          // return loadingStateItem && loadingStateItem.ready ? 'completed' : 'pending'
          return 'completed';
        };

        return {
          ...item,
          rowIndex: index,
          status: getStatus(),
        };
      });

      setRows(mappedData);
    })();
  }, [hierarchyData]);

  useDeepCompareEffect(() => {
    onAction({ actionType: FolderManagerActionTypes.SELECT, payload: localSelectedItems });
  }, [localSelectedItems]);
  //endregion

  return (
    <StyledMainContainer $detailsOpened={detailsOpened}>
      <NgCardContent>
        {!hideHeader && (
          <NgCardHeader
            ref={topBarRef}
            noBorder
            hideFilters
            filters={
              <FolderManagerHeader
                filters={filters}
                onAction={onAction}
                selectedItems={selectedItems}
                onClear={() => setSelectedItems([])}
                isLoading={isLoading}
              />
            }
          >
            <ViewTypeSwitcher activeView={activeView} onChange={setActiveView} />

            {!hideDetails && (
              <IconButtonRounded
                active={detailsOpened}
                title="Details"
                icon={<img src={chevronIcon} alt="Chevron" />}
                onClick={() => setDetailsOpened((prev) => !prev)}
                activeAccent
                flipIcon
              />
            )}
          </NgCardHeader>
        )}

        <div>
          {activeView === FolderManagerViewTypes.LIST ? (
            <FolderManagerTableView
              items={filteredRows}
              columns={columns}
              minHeight={minHeight}
              noDataMessage={noDataMessage}
              selectedRows={selectedItems}
              isLoading={isLoading}
              onAction={onAction}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onSelect={handleRowSelect}
              onDoubleClick={handleRowDoubleClick}
              storageKey={location.pathname.substring(1)}
              disableUpload={disableUpload}
            />
          ) : (
            <FolderManagerGridView
              items={filteredRows}
              selectedRows={selectedItems}
              isLoading={isLoading}
              minHeight={minHeight}
              onAction={onAction}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onSelect={handleRowSelect}
              onDoubleClick={handleRowDoubleClick}
              disableUpload={disableUpload}
            />
          )}
        </div>
      </NgCardContent>

      {detailsOpened && <DetailsRail selectedItems={selectedItems} onClose={() => setDetailsOpened(false)} />}
    </StyledMainContainer>
  );
};
